import { memo, useCallback } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { Toggle } from '@/lib/base/forms/toggle';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import {
  SCOPE_LABEL_ALL_EXCEPT_PRIVATE,
  SCOPE_LABEL_ALL_INCLUDING_PRIVATE,
} from '@/lib/permissions/scope-labels';

import type { CredentialReach } from './api-access-credentials';
import { grantRowGridClass } from './api-access-grant-layout';

/**
 * The hint labels are passive text, but the tooltip only opens from a
 * react-aria trigger (a bare span never receives the hover/focus props), so
 * they render through `TooltipTrigger` — which also makes the hint reachable
 * by keyboard.
 */
const HINT_TRIGGER_CLASSES =
  'cursor-default text-sm text-[var(--color-text-secondary)]';
/** Why a synced type offers "Can link" rather than "Can write" — shown on hover, not in the list intro. */
const SYNCED_LINKS_ONLY_HINT =
  'Synced data such as meetings and email threads can be linked to other records, but not created or changed here.';
/** Why a type offers no write toggle at all. */
const SYNCED_READ_ONLY_HINT =
  'Synced or system data: this key can read it but never change it.';
/** Explains what flipping the org-wide "include private" toggle does. */
const INCLUDE_PRIVATE_HINT =
  "Private records are visible only to the person who created them. Turning this on lets the service account read everyone's private records, not just yours.";

/** The resulting-value label for the togglable scope column. */
function togglableScopeResultingLabel(
  isOrgReach: boolean,
  includePrivate: boolean
) {
  if (!isOrgReach) {
    return 'Include private';
  }
  return includePrivate
    ? SCOPE_LABEL_ALL_INCLUDING_PRIVATE
    : SCOPE_LABEL_ALL_EXCEPT_PRIVATE;
}

/**
 * What a grant on this type actually reaches.
 *
 * - `unrestricted` — the creator's read on this type carries NO scoping, so the
 *   grant reads every record of it in the org. Stated explicitly: it is the
 *   broadest thing this screen can hand out, and it will matter more once team
 *   permissioning lands and "unscoped" stops being the common case.
 * - `togglable` — the creator holds both public and private scopings, so the
 *   choice is real and the toggle is offered.
 * - `private-only` — no public scoping exists, so granting this type can ONLY
 *   read the creator's own records. `handleCheckboxToggle` forces
 *   `includePrivate` on; the row states that rather than leaving it silent.
 * - `public-only` — only org-shared records are reachable.
 * - `unresolved` — scopings have not resolved for this type; say nothing rather
 *   than guess at the breadth.
 */
export type ScopeKind =
  | 'unrestricted'
  | 'togglable'
  | 'private-only'
  | 'public-only'
  | 'unresolved';

/**
 * What a write grant on this type can reach — decided by the schema and the
 * credential property, not by the creator's own grants.
 *
 * - `full` — create, update, delete and link: the ordinary editable record.
 * - `links-only` — the records are synced (meetings, say) and cannot be created
 *   or changed here, but they CAN be linked to and unlinked from other records.
 * - `none` — read-only on this surface: synced data, or a type the credential
 *   property excludes from writes outright (web visits). No toggle is offered;
 *   the row says why, so the missing control does not read as an omission.
 */
export type WriteKind = 'full' | 'links-only' | 'none';

/**
 * A type with no public scoping can ONLY be granted via its private
 * scopings — granting it without includePrivate would mint a grant that
 * reads nothing.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper reused by ApiAccessGrantPicker, not a component
export function isPrivateOnlyScoping(scopings: {
  unrestricted: boolean;
  publicScopings: unknown[];
}): boolean {
  return !scopings.unrestricted && scopings.publicScopings.length === 0;
}

interface GrantRowProps {
  entityTypeName: string;
  label: string;
  isSelected: boolean;
  scopeKind: ScopeKind;
  includePrivate: boolean;
  showWriteColumn: boolean;
  writeKind: WriteKind;
  includeWrite: boolean;
  /** Whose private records "Include private" reaches — the creator's (`user`) or every user's (`org`). */
  reach: CredentialReach;
  onToggle: (entityTypeName: string, isSelected: boolean) => void;
  onIncludePrivateChange: (
    entityTypeName: string,
    includePrivate: boolean
  ) => void;
  onIncludeWriteChange: (entityTypeName: string, includeWrite: boolean) => void;
}

/**
 * One grantable type: the tick that grants it and the scope that grant carries,
 * on a single row.
 *
 * These were previously two lists — every type as a checkbox, then a SECOND
 * list below repeating the ticked ones with their toggles. Ticking a type made
 * its label appear twice on screen, several rows apart, and the scope control
 * for a type sat nowhere near the type itself.
 */
function GrantRowBase({
  entityTypeName,
  label,
  isSelected,
  scopeKind,
  includePrivate,
  showWriteColumn,
  writeKind,
  includeWrite,
  reach,
  onToggle,
  onIncludePrivateChange,
  onIncludeWriteChange,
}: GrantRowProps) {
  const isOrgReach = reach === 'org';
  const handleCheckboxChange = useCallback(
    (checked: boolean) => onToggle(entityTypeName, checked),
    [entityTypeName, onToggle]
  );
  const handleToggleChange = useCallback(
    (next: boolean) => onIncludePrivateChange(entityTypeName, next),
    [entityTypeName, onIncludePrivateChange]
  );
  const handleWriteToggleChange = useCallback(
    (next: boolean) => onIncludeWriteChange(entityTypeName, next),
    [entityTypeName, onIncludeWriteChange]
  );

  // A subgrid of the picker's list (see api-access-grant-layout), so the
  // Records and Write cells line up down the list and the column headers
  // carry the repeated words — a row no longer has to say "Can write" ten
  // times. The type label takes whatever the two content-sized columns leave
  // and truncates rather than pushing a toggle past the panel edge. The row
  // carries no border of its own: the picker frames the whole list once and
  // divides rows.
  return (
    <div className={`${grantRowGridClass(showWriteColumn)} px-4 py-2.5`}>
      <div className='min-w-0 truncate'>
        <Checkbox
          isSelected={isSelected}
          onChange={handleCheckboxChange}
          label={label}
        />
      </div>
      <div className='flex items-center justify-end gap-2'>
        {isSelected && scopeKind === 'togglable' && (
          <>
            <span className='text-sm text-[var(--color-text-secondary)]'>
              {togglableScopeResultingLabel(isOrgReach, includePrivate)}
            </span>
            {isOrgReach ? (
              <Tooltip
                title={INCLUDE_PRIVATE_HINT}
                interactive={true}
                placement='top'
              >
                <Toggle
                  isSelected={includePrivate}
                  onChange={handleToggleChange}
                  aria-label={`Include everyone's private ${label} records`}
                />
              </Tooltip>
            ) : (
              <Toggle
                isSelected={includePrivate}
                onChange={handleToggleChange}
                aria-label={`Include private ${label} records`}
              />
            )}
          </>
        )}
        {isSelected && scopeKind === 'unrestricted' && (
          <span className='text-sm text-[var(--color-text-secondary)]'>
            All records
          </span>
        )}
        {isSelected && scopeKind === 'private-only' && (
          <span className='text-sm text-[var(--color-text-secondary)]'>
            {isOrgReach ? "Every user's records" : 'Your records only'}
          </span>
        )}
        {isSelected && scopeKind === 'public-only' && (
          <span className='text-sm text-[var(--color-text-secondary)]'>
            Shared records only
          </span>
        )}
      </div>
      {showWriteColumn && (
        <div className='flex items-center justify-end gap-2'>
          {isSelected && writeKind === 'full' && (
            <Toggle
              isSelected={includeWrite}
              onChange={handleWriteToggleChange}
              aria-label={`Allow ${label} writes`}
            />
          )}
          {isSelected && writeKind === 'links-only' && (
            <>
              <Tooltip title={SYNCED_LINKS_ONLY_HINT}>
                <TooltipTrigger className={HINT_TRIGGER_CLASSES}>
                  Links only
                </TooltipTrigger>
              </Tooltip>
              <Toggle
                isSelected={includeWrite}
                onChange={handleWriteToggleChange}
                aria-label={`Allow ${label} links`}
              />
            </>
          )}
          {isSelected && writeKind === 'none' && (
            <Tooltip title={SYNCED_READ_ONLY_HINT}>
              <TooltipTrigger className={HINT_TRIGGER_CLASSES}>
                Read-only
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}

export const GrantRow = memo(GrantRowBase);
